import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LogOut, User, Menu, X, Languages, Sparkles } from "lucide-react";
import { useState } from "react";
import { isPremium } from "@/lib/premium";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const loggedIn = user && user !== false;

  const links = [
    { to: "/learn/hiragana", label: t("hiragana"), id: "hiragana" },
    { to: "/learn/katakana", label: t("katakana"), id: "katakana" },
    { to: "/learn/kanji", label: t("kanji"), id: "kanji" },
    { to: "/practice", label: t("flashcards"), id: "flashcards" },
    { to: "/pdf", label: t("pdfSheets"), id: "pdf-sheets" },
  ];

  const toggleLang = () => setLang(lang === "en" ? "pt" : "en");
  const doLogout = async () => { await logout(); setOpen(false); navigate("/"); };

  return (
    <header className="sticky top-3 sm:top-4 z-50 px-3 sm:px-6">
      <nav className="kawaii-card max-w-7xl mx-auto flex items-center justify-between gap-3 pl-3 pr-3 sm:pl-4 sm:pr-4 py-2 !rounded-full">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img src="/brand/logo.png" alt="Nihongo Kawaii" className="glass-orb w-10 h-10 sm:w-11 sm:h-11 object-cover" />
          <span className="font-display text-lg sm:text-xl tracking-tight text-ink hidden xs:inline sm:inline">
            Nihongo <span className="text-sakura-deep">Kawaii</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.id}`}
              className={({ isActive }) => `kawaii-pill-nav ${isActive ? "active" : ""}`}>
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/pricing" data-testid="nav-premium"
            className={({ isActive }) => `kawaii-pill-nav flex items-center gap-1.5 ${isActive ? "active" : "!text-sakura-deep"}`}>
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("premium")}
          </NavLink>
        </div>

        <div className="hidden lg:flex items-center gap-2">
          <button data-testid="lang-toggle" onClick={toggleLang} className="kawaii-btn kawaii-btn-sm uppercase" title="Language / Idioma">
            <Languages className="w-3.5 h-3.5" strokeWidth={2.5} /> {lang}
          </button>
          {loggedIn ? (
            <>
              <button data-testid="nav-dashboard-btn" onClick={() => navigate("/dashboard")} className="kawaii-btn kawaii-btn-sm bg-matcha/70">
                <User className="w-4 h-4" strokeWidth={2.5} /> {user.name?.split(" ")[0] || "You"}
                {isPremium(user) && <Sparkles className="w-3.5 h-3.5 text-sakura-deep" strokeWidth={2.5} />}
              </button>
              <button data-testid="nav-logout-btn" onClick={doLogout} className="kawaii-btn kawaii-btn-sm kawaii-btn-ghost" title={t("logout")}>
                <LogOut className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="kawaii-btn kawaii-btn-sm kawaii-btn-ghost">{t("login")}</Link>
              <Link to="/register" data-testid="nav-signup-btn" className="kawaii-btn kawaii-btn-sm kawaii-btn-ink">{t("signup")}</Link>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}
          className="lg:hidden kawaii-btn !p-2.5 !rounded-full" aria-label="Menu">
          {open ? <X className="w-5 h-5" strokeWidth={2.5} /> : <Menu className="w-5 h-5" strokeWidth={2.5} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden kawaii-card max-w-7xl mx-auto mt-2 p-4 flex flex-col gap-1 rise-in" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
              className={({ isActive }) => `kawaii-pill-nav !py-3 ${isActive ? "active" : ""}`}>
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/pricing" onClick={() => setOpen(false)} data-testid="nav-premium-mobile"
            className={({ isActive }) => `kawaii-pill-nav !py-3 flex items-center gap-2 ${isActive ? "active" : "!text-sakura-deep"}`}>
            <Sparkles className="w-4 h-4" strokeWidth={2.5} /> {t("premium")}
          </NavLink>
          <div className="flex flex-wrap gap-2 pt-3 mt-2 border-t border-ink/5">
            <button data-testid="lang-toggle-mobile" onClick={toggleLang} className="kawaii-btn kawaii-btn-sm bg-lavender/60">
              <Languages className="w-4 h-4" strokeWidth={2.5} /> {lang === "en" ? "English" : "Português"}
            </button>
            {loggedIn ? (
              <>
                <button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="kawaii-btn kawaii-btn-sm bg-matcha/70 flex-1">{t("dashboard")}</button>
                <button onClick={doLogout} className="kawaii-btn kawaii-btn-sm">{t("logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="kawaii-btn kawaii-btn-sm flex-1">{t("login")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="kawaii-btn kawaii-btn-sm kawaii-btn-ink flex-1">{t("signup")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
